import type { FC } from "react";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { type Id, Slide, toast } from "react-toastify";

import Typography from "@/design-system/Typography";
import { CloseIconSlimButton } from "@/design-system/icons/close-icon-slim";
import TelemetryButton from "@/design-system/telemetry-button";
import { AccountNumber } from "@/src/common/AccountNumber";
import { languageSelector } from "@/src/contexts";
import { PasswordSettingsResources } from "@/src/settings/PasswordSettings.Resources";
import { PasswordSettingsResourcesEn } from "@/src/settings/PasswordSettings.Resources.En";
import { SettingsResources } from "@/src/settings/Settings.Resources";
import { SettingsResourcesEn } from "@/src/settings/Settings.Resources.En";
import { type IErrorMessage } from "@Components/ErrorMessage";
import type { IAjax } from "@Components/Hooks/useAjax";
import useAjax from "@Components/Hooks/useAjax";
import { Loading } from "@Components/Loading";
import { AjaxMethod } from "@Components/Modules/Ajax";
import Text from "@Components/Text";
import { TextboxAppearanceV2, TextboxV2 } from "@Components/Textbox";
import * as stylex from "@stylexjs/stylex";

import type { IContentContainer } from "../common/ContentContainer";
import MillenniumBankSVG from "../common/icons/MillenniumBankSVG";
import { ComposableSpacing, colors } from "./../../styles/tokens.stylex";
import type { IAccountSettings } from "./AccountSettings";
import type { IGDPRFile } from "./GDPRFiles";
import type { INewsletterSettings } from "./NewsletterSettings";

import * as portalCss from "../shared/Portal.module.css";
import * as css from "./SettingsBank.module.css";

export interface ISettingsBank {
    accountSettings: IAccountSettings;
    bankUser360BannerTitle: string;
    saveChangesSuccessMessage: string;
    myProfileMessage: string;
    contentContainers?: IContentContainer;
    isBankUser: boolean;
    cashbackDetailsSettings: ICashbackDetailsSettings;
    files: IGDPRFile[];
    newsletterMessage: string;
    newsletterSettings: INewsletterSettings;
}

interface ICashbackDetailsSettings extends IAjax<boolean> {
    aliasRequiredError: string;
    firstLastNameInvalidError: string;
    firstLastNameInvalidCharsError: string;
    accountNumberInvalidError: string;
    bankInfoInvalidError: string;
}

interface ISettingsForm {
    accountNumber: string;
    accountRecipient: string;
}

const SettingsV2: FC<ISettingsBank> = (props) => {
    const resources = languageSelector(SettingsResources, SettingsResourcesEn);
    const resourcesPw = languageSelector(PasswordSettingsResources, PasswordSettingsResourcesEn);
    const toastId = useRef<Id>(null);

    useEffect(
        () => () => {
            if (toastId.current) toast.dismiss(toastId.current);
        },
        [],
    );

    const {
        handleSubmit,
        formState: { errors },
        register,
        setValue,
        setError,
    } = useForm<ISettingsForm>({
        defaultValues: {
            accountNumber: props.accountSettings.accountNumber.accountNumberTextbox.properties.value || "",
            accountRecipient: props.accountSettings.firstLastNameTextbox.properties.value || "",
        },
    });

    const showSettingsSuccess = (message: string) => {
        toast.dismiss(toastId.current || undefined);

        toastId.current = toast(
            ({ closeToast }) => {
                return (
                    <>
                        <CloseIconSlimButton styleX={styles.successMessageCloseIcon} onClick={closeToast} />
                        <Typography textElement="p" fontStyle="BodyL" styleX={styles.successMessageText}>
                            {message}
                        </Typography>
                    </>
                );
            },
            {
                autoClose: false,
                position: "bottom-center",
                closeButton: false,
                className: stylex.props(styles.successToastContainer).className,
                ariaLabel: "success-saved-changes",
                hideProgressBar: true,
                transition: Slide,
            },
        );
    };

    const { request, isLoading } = useAjax<IErrorMessage | boolean>({
        ajaxAction: "/api/settings/%7Baction%7D",
        ajaxMethod: AjaxMethod.Post,
        success: (res) => {
            const err = res as IErrorMessage;
            if (err.errorName) {
                setError(err.errorName as keyof ISettingsForm, { message: err.message });
                return;
            }

            if (res === true) {
                showSettingsSuccess(resourcesPw.saveChangesMessage);
            }
        },
    });

    const onSubmit = (values: ISettingsForm) => {
        request(
            { action: "ChangeCashbackAccountSettings" },
            {
                accountRecipient: values.accountRecipient,
                accountNumber: values.accountNumber.replaceAll(" ", ""),
            },
        );
    };

    const validateFirstLastName = (value: string) => {
        const cleanValue = value.trim();

        if (!cleanValue || cleanValue.length > 90 || cleanValue.split(" ").length === 1) {
            return resources.firstLastNameInvalidError;
        }

        if (
            !/^[a-z0-9]+ {1}[a-z0-9]+(?:[ -]{1}[a-z0-9]+)?$/i.test(
                cleanValue
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .replace(/\u0142/g, "l"),
            )
        ) {
            return resources.firstLastNameInvalidError;
        }
    };

    const formatAccountNumber = (inputStr: string) => {
        const account = inputStr.trim().replace(/[^0-9]/gi, "");

        if (!account) {
            return "";
        }

        const parts = [];

        parts.push(account.substring(0, 2));

        for (let r = 2; r < account.length; r += 4) {
            parts.push(account.substring(r, r + 4));
        }

        return parts.join(" ");
    };

    const updateAccountNumber = (acc: string) => {
        const formatted = formatAccountNumber(acc);

        setValue("accountNumber", formatted);
    };

    return (
        <div className={`${css.settingsBankComponent} ${portalCss.standardContainerV2}`}>
            <div className={`${css.settingsContainer} ${stylex.props(styles.bottomSpace).className}`}>
                {props.bankUser360BannerTitle && (
                    <div className={css.banner}>
                        <div className={css.icon}>
                            <MillenniumBankSVG />
                        </div>
                        <span dangerouslySetInnerHTML={{ __html: props.bankUser360BannerTitle }} />
                    </div>
                )}

                <form className={css.cashbackDetailsForm} onSubmit={handleSubmit(onSubmit)}>
                    <p className={`${portalCss.h3Font} ${css.title}`}>{props.myProfileMessage}</p>

                    <span>{resources.firstLastNameLabel}</span>
                    <TextboxV2
                        {...register("accountRecipient", {
                            required: resources.firstLastNameInvalidError,
                            maxLength: { value: 90, message: resources.firstLastNameInvalidError },
                            validate: validateFirstLastName,
                        })}
                        appearance={TextboxAppearanceV2.Standard}
                        errorMessage={errors.accountRecipient?.message}
                    />

                    <AccountNumber {...props.accountSettings.accountNumber}>
                        <TextboxV2
                            {...register("accountNumber", {})}
                            appearance={TextboxAppearanceV2.Standard}
                            onChange={(ev) => updateAccountNumber(ev.target.value)}
                            onPaste={(ev) => {
                                const acc = ev.clipboardData.getData("text/plain");
                                updateAccountNumber(acc);
                                ev.preventDefault();
                            }}
                            errorMessage={errors.accountNumber?.message}
                        />
                    </AccountNumber>

                    {errors.root?.message && (
                        <Text textElement="p" className={portalCss.hasError}>
                            {errors.root?.message}
                        </Text>
                    )}

                    <TelemetryButton
                        as="button"
                        variant="magentaRound"
                        type="submit"
                        size="M"
                        eventName="common-save"
                    >
                        {resources.saveChanges}
                    </TelemetryButton>

                    {isLoading && <Loading isModal />}
                </form>
            </div>
        </div>
    );
};

export default SettingsV2;

const styles = stylex.create({
    bottomSpace: {
        marginBottom: "50px",
    },

    // successMessageCloseIcon: {
    //     position: "absolute",
    //     width: "20px",
    //     height: "20px",
    //     right: "20px",
    // },

    successToastContainer: {
        borderRadius: ComposableSpacing.s2,

        gap: ComposableSpacing.s2,
        alignItems: "center",
        backgroundColor: "#1b2670",
        boxShadow: "0 3px 10px rgba(0, 0, 0, 0.1), 0 3px 3px rgba(0, 0, 0, 0.05)",
        flexDirection: "row-reverse",

        marginBottom: ComposableSpacing.s6,
        minHeight: "2rem",
        paddingBottom: ComposableSpacing.s3,
        paddingLeft: ComposableSpacing.s5,
        paddingRight: ComposableSpacing.s5,
        paddingTop: ComposableSpacing.s3,
        width: "90vw",
    },

    successMessageText: {
        flex: "1",
        color: colors.fg_AltDefault,
        textAlign: "center",
    },

    successMessageCloseIcon: {
        color: colors.fg_AltDefault,
        height: 20,
        width: 20,
    },
});
